import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Package, Shield, Palette, Check, X, Zap, Clock, Lock, Crown, Heart, Sword, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fruitCrush_token';

// Item definitions matching backend
const ITEM_INFO = {
  // Consumables
  'extra_moves': { name: 'Extra Moves +5', emoji: '➕', type: 'consumable', description: 'Add 5 extra moves' },
  'shuffle': { name: 'Board Shuffle', emoji: '🔀', type: 'consumable', description: 'Shuffle the board' },
  'color_bomb': { name: 'Color Bomb', emoji: '🌈', type: 'consumable', description: 'Clear all of one fruit type' },
  'row_blast': { name: 'Row Blast', emoji: '💥', type: 'consumable', description: 'Clear an entire row' },
  'hammer': { name: 'Hammer', emoji: '🔨', type: 'consumable', description: 'Break any single tile' },
  'double_xp': { name: '2X XP Boost', emoji: '⚡', type: 'consumable', description: 'Double XP for next level' },
  'wrapped_fruit': { name: 'Wrapped Fruit', emoji: '🎁', type: 'consumable', description: 'Start with a wrapped candy' },
  'striped_fruit': { name: 'Striped Fruit', emoji: '🍬', type: 'consumable', description: 'Start with a striped candy' },
  'health_potion': { name: 'Health Potion', emoji: '🧪', type: 'consumable', description: 'Restore 40 HP' },
  'super_health_potion': { name: 'Super Health Potion', emoji: '💊', type: 'consumable', description: 'Restore 80 HP' },
  'bomb': { name: 'Bomb', emoji: '💣', type: 'consumable', description: 'Destroy 3x3 area' },
  'disco_fruit': { name: 'Disco Fruit', emoji: '🪩', type: 'consumable', description: 'Place a disco melon' },
  
  // Milestone Reward Items (drops)
  'crystal_ball': { name: 'Crystal Ball', emoji: '🔮', type: 'consumable', description: 'Preview next 3 rows of fruits', rarity: 'rare' },
  'fruit_magnet': { name: 'Fruit Magnet', emoji: '🧲', type: 'consumable', description: 'Pull matching fruits together', rarity: 'epic' },
  'dragon_scale': { name: 'Dragon Scale', emoji: '🐉', type: 'consumable', description: '+50% boss damage', rarity: 'epic' },
  'moon_stone': { name: 'Moon Stone', emoji: '🌙', type: 'consumable', description: 'Highlights best moves', rarity: 'rare' },
  'thunder_strike': { name: 'Thunder Strike', emoji: '⚡', type: 'consumable', description: 'Clear column + row', rarity: 'legendary' },
  
  // Achievement Reward Items (drops)
  'lucky_clover': { name: 'Lucky Clover', emoji: '🍀', type: 'consumable', description: '+15% special spawn rate', rarity: 'rare' },
  'guardian_shield': { name: 'Guardian Shield', emoji: '🛡️', type: 'consumable', description: 'Block 1 boss attack', rarity: 'epic' },
  'tsunami_wave': { name: 'Tsunami Wave', emoji: '🌊', type: 'consumable', description: 'Clear bottom 2 rows', rarity: 'epic' },
  'rainbow_aura': { name: 'Rainbow Aura', emoji: '🌈', type: 'consumable', description: 'Start with special fruit', rarity: 'rare' },
  
  // Frozen Peaks Drops
  'yeti_fur': { name: 'Yeti Fur', emoji: '🦣', type: 'consumable', description: 'Immune to freeze for 5 turns', rarity: 'legendary' },
  'snow_globe': { name: 'Snow Globe', emoji: '🔮', type: 'consumable', description: 'Freeze all obstacles for 3 turns', rarity: 'epic' },
  'ice_pick': { name: 'Ice Pick', emoji: '⛏️', type: 'consumable', description: 'Break any frozen tile instantly', rarity: 'rare' },
  
  // Ancient Ruins Drops
  'pharaoh_scarab': { name: "Pharaoh's Scarab", emoji: '🪲', type: 'consumable', description: 'Remove all curse tiles', rarity: 'epic' },
  'ankh_of_life': { name: 'Ankh of Life', emoji: '☥', type: 'consumable', description: 'Continue level after losing', rarity: 'legendary' },
  'eye_of_ra': { name: 'Eye of Ra', emoji: '👁️', type: 'consumable', description: 'Reveal hidden matches', rarity: 'rare' },
  'sand_timer': { name: 'Sand Timer', emoji: '⏳', type: 'consumable', description: 'Add 30 seconds to timed levels', rarity: 'rare' },
  
  // Ocean Depths Drops
  'ocean_pearl': { name: 'Ocean Pearl', emoji: '🦪', type: 'consumable', description: 'Create a whirlpool clearing adjacent', rarity: 'rare' },
  'trident_shard': { name: 'Trident Shard', emoji: '🔱', type: 'consumable', description: 'Strike 3 random tiles', rarity: 'epic' },
  'mermaid_tear': { name: 'Mermaid Tear', emoji: '💧', type: 'consumable', description: 'Converts 5 tiles to water orbs', rarity: 'legendary' },
  'coral_charm': { name: 'Coral Charm', emoji: '🪸', type: 'consumable', description: 'Slows bubble rise for 5 turns', rarity: 'rare' },
  
  // Equipment
  'god_sword': { name: 'God Sword', emoji: '⚔️', type: 'equipment', description: 'Clears entire board (5 uses per level)', rarity: 'legendary', uses: 5 },
  'lucky_charm': { name: 'Lucky Charm', emoji: '🍀', type: 'equipment', description: '+10% chance for special candies', rarity: 'rare' },
  'double_coins': { name: 'Coin Doubler', emoji: '💰', type: 'equipment', description: 'Double coins earned from levels', rarity: 'epic' },
  'xp_ring': { name: 'XP Ring', emoji: '💍', type: 'equipment', description: '+25% XP from levels', rarity: 'rare' },
  'lucky_dice': { name: 'Lucky Dice', emoji: '🎲', type: 'equipment', description: 'Better starting boards', rarity: 'epic' },
  
  // Sword Equipment (tradeable once unlocked)
  'bronze_sword': { name: 'Bronze Sword', emoji: '🥉', type: 'sword', description: '3x3 area clear (Stage 3)', rarity: 'common', tier: 'bronze', size: 3 },
  'silver_sword': { name: 'Silver Sword', emoji: '🥈', type: 'sword', description: '4x4 area clear (Stage 20)', rarity: 'rare', tier: 'silver', size: 4 },
  'golden_sword': { name: 'Golden Sword', emoji: '🥇', type: 'sword', description: '5x5 area clear (Stage 50)', rarity: 'epic', tier: 'golden', size: 5 },
  'diamond_sword': { name: 'Diamond Sword', emoji: '💎', type: 'sword', description: '7x7 area clear (Stage 100)', rarity: 'legendary', tier: 'diamond', size: 7 },
  'master_sword': { name: 'Master Sword', emoji: '👑', type: 'sword', description: '8x8 area clear (Stage 150)', rarity: 'legendary', tier: 'master', size: 8 },
};

// Fruit definitions for collected fruits
const FRUIT_INFO = {
  'fruit_apple': { name: 'Apple', emoji: '🍎' },
  'fruit_banana': { name: 'Banana', emoji: '🍌' },
  'fruit_orange': { name: 'Orange', emoji: '🍊' },
  'fruit_grape': { name: 'Grape', emoji: '🍇' },
  'fruit_strawberry': { name: 'Strawberry', emoji: '🍓' },
  'fruit_watermelon': { name: 'Watermelon', emoji: '🍉' },
  'fruit_peach': { name: 'Peach', emoji: '🍑' },
  'fruit_pear': { name: 'Pear', emoji: '🍐' },
  'fruit_cherry': { name: 'Cherry', emoji: '🍒' },
  'fruit_lemon': { name: 'Lemon', emoji: '🍋' },
  'fruit_mango': { name: 'Mango', emoji: '🥭' },
  'fruit_pineapple': { name: 'Pineapple', emoji: '🍍' },
  'fruit_coconut': { name: 'Coconut', emoji: '🥥' },
  'fruit_kiwi': { name: 'Kiwi', emoji: '🥝' },
  'fruit_blueberry': { name: 'Blueberry', emoji: '🫐' },
};

// Rarity styles
const RARITY_STYLES = {
  common: 'border-gray-300 bg-gray-50',
  rare: 'border-blue-400 bg-blue-50',
  epic: 'border-purple-400 bg-purple-50',
  legendary: 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-100'
};

export function InventoryModal({ isOpen, onClose, inventory, equipment, equippedItems, cosmetics, collectedFruits, onEquip, onUnequip, onRecharge, isPremium = false, onUseItem, hp, maxHP, playerLevel = 1, equippedSword, onEquipSword, playerName = 'Player' }) {
  const [activeTab, setActiveTab] = useState('consumables');
  const [equipmentStatuses, setEquipmentStatuses] = useState({});
  const [loadingRecharge, setLoadingRecharge] = useState(null);
  const [usingItem, setUsingItem] = useState(null);
  const [showSwordDropdown, setShowSwordDropdown] = useState(false);
  
  // Fetch equipment statuses on mount
  useEffect(() => {
    if (isOpen && equipment && equipment.length > 0) {
      const fetchStatuses = async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return;
        
        const statuses = {};
        for (const itemId of [...new Set(equipment)]) {
          try {
            const response = await fetch(`${API_URL}/api/equipment/status/${itemId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              statuses[itemId] = await response.json();
            }
          } catch (error) {
            console.error(`Failed to fetch status for ${itemId}:`, error);
          }
        }
        setEquipmentStatuses(statuses);
      };
      fetchStatuses();
    }
  }, [isOpen, equipment]);
  
  if (!isOpen) return null;

  const getItemInfo = (itemId) => {
    return ITEM_INFO[itemId] || { name: itemId, emoji: '📦', type: 'unknown', description: 'Unknown item' };
  };

  // Get consumables from inventory
  const consumables = Object.entries(inventory || {})
    .filter(([id, qty]) => qty > 0)
    .map(([id, qty]) => ({ id, quantity: qty, ...getItemInfo(id) }));

  // Get collected fruits
  const fruits = Object.entries(collectedFruits || {})
    .filter(([id, qty]) => qty > 0)
    .map(([id, qty]) => ({ 
      id, 
      quantity: qty, 
      ...(FRUIT_INFO[id] || { name: id.replace('fruit_', '').replace(/_/g, ' '), emoji: '🍎' })
    }));

  // Get equipment items
  const equipmentItems = [];
  const equipmentCount = {};
  (equipment || []).forEach(itemId => {
    equipmentCount[itemId] = (equipmentCount[itemId] || 0) + 1;
  });
  Object.entries(equipmentCount).forEach(([id, qty]) => {
    equipmentItems.push({ id, quantity: qty, ...getItemInfo(id) });
  });

  // Check if item is equipped
  const isEquipped = (itemId) => {
    return Object.values(equippedItems || {}).includes(itemId);
  };

  // Handle equip/unequip
  const handleToggleEquip = async (itemId) => {
    if (isEquipped(itemId)) {
      await onUnequip(itemId);
    } else {
      await onEquip(itemId);
    }
  };

  // Get all cosmetic items from different categories
  const cosmeticItems = [
    // Skins
    ...(cosmetics?.unlocked_skins || []).map(id => ({
      id,
      name: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      emoji: '🎨',
      type: 'skin',
      category: 'Skins'
    })),
    // Effects
    ...(cosmetics?.unlocked_effects || []).map(id => ({
      id,
      name: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      emoji: '✨',
      type: 'effect',
      category: 'Effects'
    })),
    // Themes
    ...(cosmetics?.unlocked_themes || []).map(id => ({
      id,
      name: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      emoji: '🖼️',
      type: 'theme',
      category: 'Themes'
    })),
    // Frames
    ...(cosmetics?.unlocked_frames || []).map(id => ({
      id,
      name: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      emoji: '🖼️',
      type: 'frame',
      category: 'Frames'
    })),
  ];

  // Group cosmetics by category
  const cosmeticsByCategory = cosmeticItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header with Player Info */}
        <div className="p-4 border-b bg-gradient-to-r from-indigo-500 to-purple-500 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
              <Package className="w-6 h-6" />
              Inventory
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* Player Name and Health Bar */}
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-white text-sm">{playerName}</span>
              <span className="text-xs text-white/70">Stage {playerLevel}</span>
            </div>
            
            {/* Health Bar - Prominent */}
            {hp !== undefined && maxHP !== undefined && (
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-300 fill-red-300" />
                <div className="flex-1 h-4 bg-white/20 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      hp / maxHP > 0.6 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                      hp / maxHP > 0.3 ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                      'bg-gradient-to-r from-red-400 to-red-600'
                    }`}
                    style={{ width: `${(hp / maxHP) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-white min-w-[50px] text-right">{hp}/{maxHP}</span>
              </div>
            )}
            
            {/* Sword Selection Dropdown */}
            <div className="mt-3 relative">
              <button
                onClick={() => setShowSwordDropdown(!showSwordDropdown)}
                className="w-full flex items-center justify-between p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sword className="w-4 h-4 text-yellow-300" />
                  <span className="text-sm font-semibold text-white">
                    {equippedSword ? ITEM_INFO[equippedSword]?.name || 'Select Sword' : 'Select Sword'}
                  </span>
                  {equippedSword && (
                    <span className="text-lg">{ITEM_INFO[equippedSword]?.emoji}</span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-white transition-transform ${showSwordDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Sword Dropdown Options */}
              {showSwordDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl z-50 overflow-hidden border">
                  {[
                    { id: 'bronze_sword', level: 3 },
                    { id: 'silver_sword', level: 20 },
                    { id: 'golden_sword', level: 50 },
                    { id: 'diamond_sword', level: 100 },
                    { id: 'master_sword', level: 150 },
                  ].map(sword => {
                    const info = ITEM_INFO[sword.id];
                    const isUnlocked = playerLevel >= sword.level;
                    const isSelected = equippedSword === sword.id;
                    
                    return (
                      <button
                        key={sword.id}
                        onClick={() => {
                          if (isUnlocked && onEquipSword) {
                            onEquipSword(sword.id);
                            setShowSwordDropdown(false);
                          }
                        }}
                        disabled={!isUnlocked}
                        className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                          isSelected ? 'bg-indigo-100 border-l-4 border-indigo-500' :
                          isUnlocked ? 'hover:bg-gray-50' : 'bg-gray-100 opacity-60'
                        }`}
                      >
                        <span className="text-2xl">{info.emoji}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{info.name}</span>
                            {isSelected && <Check className="w-4 h-4 text-indigo-500" />}
                          </div>
                          <span className="text-xs text-gray-500">{info.description}</span>
                        </div>
                        {!isUnlocked && (
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Lock className="w-3 h-3" />
                            <span>Stage {sword.level}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0">
          {[
            { id: 'consumables', label: 'Items', icon: <Package className="w-4 h-4" />, count: consumables.length },
            { id: 'fruits', label: 'Fruits', icon: <span className="text-sm">🍎</span>, count: fruits.length },
            { id: 'equipment', label: 'Equipment', icon: <Shield className="w-4 h-4" />, count: equipmentItems.length },
            { id: 'cosmetics', label: 'Cosmetics', icon: <Palette className="w-4 h-4" />, count: cosmeticItems.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-2 text-sm font-semibold flex items-center justify-center gap-1 transition-colors ${
                activeTab === tab.id
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-indigo-100' : 'bg-gray-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content - with scrollbar */}
        <ScrollArea className="flex-1 min-h-0 overflow-auto">
          <div className="p-4">
          {activeTab === 'consumables' && (
            <div className="space-y-2">
              {consumables.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No consumables</p>
                  <p className="text-xs mt-1">Buy items from the Shop</p>
                </div>
              ) : (
                consumables.map(item => {
                  // Determine if item can be used from inventory
                  const isHealthPotion = item.id === 'health_potion' || item.id === 'super_health_potion';
                  const canUseFromInventory = isHealthPotion;
                  const isAtFullHealth = hp !== undefined && maxHP !== undefined && hp >= maxHP;
                  const canUsePotion = isHealthPotion && !isAtFullHealth && hp !== undefined;
                  
                  const handleUseItem = async () => {
                    if (!canUseFromInventory || !onUseItem) return;
                    
                    if (isHealthPotion && isAtFullHealth) {
                      toast.info('Already at full health!');
                      return;
                    }
                    
                    setUsingItem(item.id);
                    try {
                      const success = await onUseItem(item.id);
                      if (success) {
                        const healAmount = item.id === 'health_potion' ? 40 : 80;
                        toast.success(`Used ${item.name}! +${healAmount} HP`);
                      }
                    } catch (error) {
                      toast.error('Failed to use item');
                    } finally {
                      setUsingItem(null);
                    }
                  };
                  
                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg border transition-colors ${
                        isHealthPotion 
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:from-green-100 hover:to-emerald-100' 
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{item.emoji}</span>
                        <div className="flex-1">
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg text-indigo-600">x{item.quantity}</span>
                          {canUseFromInventory && (
                            <Button
                              size="sm"
                              onClick={handleUseItem}
                              disabled={usingItem === item.id || (isHealthPotion && isAtFullHealth)}
                              className={`h-8 text-xs ${
                                isHealthPotion && isAtFullHealth
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                              }`}
                            >
                              {usingItem === item.id ? (
                                'Using...'
                              ) : (
                                <>
                                  <Heart className="w-3 h-3 mr-1" />
                                  Use
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Fruits Tab */}
          {activeTab === 'fruits' && (
            <div className="space-y-2">
              {fruits.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <span className="text-4xl block mb-2">🍎</span>
                  <p>No fruits collected</p>
                  <p className="text-xs mt-1">Complete levels to collect fruits!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {fruits.map(fruit => (
                    <div
                      key={fruit.id}
                      className="p-3 rounded-lg border-2 border-green-300 bg-green-50 flex items-center gap-2"
                    >
                      <span className="text-2xl">{fruit.emoji}</span>
                      <div className="flex-1">
                        <p className="font-bold text-sm text-gray-800">{fruit.name}</p>
                        <p className="text-xs text-gray-500">Tradeable on Exchange</p>
                      </div>
                      <span className="bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full font-bold">
                        x{fruit.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {fruits.length > 0 && (
                <p className="text-xs text-center text-gray-400 mt-3">
                  Trade fruits on the Grand Exchange for coins!
                </p>
              )}
            </div>
          )}

          {activeTab === 'equipment' && (
            <div className="space-y-2">
              {equipmentItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No equipment</p>
                  <p className="text-xs mt-1">Buy from Grand Exchange</p>
                </div>
              ) : (
                equipmentItems.map(item => {
                  const equipped = isEquipped(item.id);
                  const status = equipmentStatuses[item.id];
                  const usesRemaining = status?.uses_remaining ?? item.uses ?? 5;
                  const maxUses = status?.max_uses ?? 5;
                  const canRecharge = status?.can_recharge;
                  const rechargeAt = status?.recharge_available_at;
                  
                  // Calculate time until recharge
                  let rechargeTimeStr = '';
                  if (rechargeAt && !canRecharge) {
                    const rechargeDate = new Date(rechargeAt);
                    const now = new Date();
                    const diffMs = rechargeDate - now;
                    if (diffMs > 0) {
                      const hours = Math.floor(diffMs / (1000 * 60 * 60));
                      const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                      rechargeTimeStr = `${hours}h ${mins}m`;
                    }
                  }
                  
                  const handleRecharge = async () => {
                    if (!canRecharge || !onRecharge) return;
                    setLoadingRecharge(item.id);
                    const result = await onRecharge(item.id);
                    if (result?.success) {
                      setEquipmentStatuses(prev => ({
                        ...prev,
                        [item.id]: { ...prev[item.id], uses_remaining: 5, can_recharge: false }
                      }));
                      toast.success('Equipment recharged!');
                    } else if (result?.message) {
                      toast.error(result.message);
                    }
                    setLoadingRecharge(null);
                  };
                  
                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        equipped 
                          ? 'border-green-400 bg-green-50' 
                          : RARITY_STYLES[item.rarity || 'common']
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{item.emoji}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">{item.name}</p>
                            {equipped && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-500 text-white rounded-full">
                                EQUIPPED
                              </span>
                            )}
                            {item.rarity === 'legendary' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-white rounded-full">
                                LEGENDARY
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{item.description}</p>
                          
                          {/* Uses indicator for God Sword */}
                          {item.id === 'god_sword' && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2">
                                <Zap className="w-3 h-3 text-yellow-500" />
                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all"
                                    style={{ width: `${(usesRemaining / maxUses) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-amber-600">{usesRemaining}/{maxUses}</span>
                              </div>
                              
                              {usesRemaining < maxUses && (
                                <div className="flex items-center gap-2 text-xs">
                                  {canRecharge ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleRecharge}
                                      disabled={loadingRecharge === item.id}
                                      className="h-6 text-xs bg-gradient-to-r from-green-400 to-emerald-500 text-white border-0 hover:from-green-500 hover:to-emerald-600"
                                    >
                                      <Zap className="w-3 h-3 mr-1" />
                                      {loadingRecharge === item.id ? 'Recharging...' : 'Recharge Now!'}
                                    </Button>
                                  ) : rechargeTimeStr && (
                                    <span className="flex items-center gap-1 text-gray-500">
                                      <Clock className="w-3 h-3" />
                                      Recharge in: {rechargeTimeStr}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-sm text-gray-500">x{item.quantity}</span>
                          <Button
                            size="sm"
                            variant={equipped ? "outline" : "default"}
                            onClick={() => handleToggleEquip(item.id)}
                            className={`h-7 text-xs ${equipped ? 'border-red-300 text-red-600 hover:bg-red-50' : 'bg-green-500 hover:bg-green-600'}`}
                          >
                            {equipped ? (
                              <><X className="w-3 h-3 mr-1" /> Unequip</>
                            ) : (
                              <><Check className="w-3 h-3 mr-1" /> Equip</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'cosmetics' && (
            <div className="space-y-4">
              {/* Premium Warning */}
              {!isPremium && cosmeticItems.length > 0 && (
                <div className="p-3 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300 flex items-center gap-3">
                  <div className="p-2 bg-amber-200 rounded-full">
                    <Lock className="w-4 h-4 text-amber-700" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-800 flex items-center gap-1">
                      <Crown className="w-4 h-4" /> Premium Required
                    </p>
                    <p className="text-xs text-amber-700">
                      Get Battle Pass Premium to equip cosmetics in-game!
                    </p>
                  </div>
                </div>
              )}
              
              {cosmeticItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Palette className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No cosmetics</p>
                  <p className="text-xs mt-1">Unlock cosmetics from Battle Pass</p>
                </div>
              ) : (
                Object.entries(cosmeticsByCategory).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="text-sm font-bold text-purple-600 mb-2 flex items-center gap-2">
                      {category === 'Skins' && '🎨'}
                      {category === 'Effects' && '✨'}
                      {category === 'Themes' && '🖼️'}
                      {category === 'Frames' && '🖼️'}
                      {category} ({items.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map(item => (
                        <div
                          key={item.id}
                          className={`p-2 rounded-lg border transition-colors relative ${
                            isPremium 
                              ? 'bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100'
                              : 'bg-gray-100 opacity-75'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{item.emoji}</span>
                            <p className="font-semibold text-xs truncate">{item.name}</p>
                          </div>
                          {!isPremium && (
                            <div className="absolute top-1 right-1">
                              <Lock className="w-3 h-3 text-gray-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          </div>
        </ScrollArea>

        {/* Footer hint */}
        <div className="p-3 border-t bg-gray-50 text-center text-xs text-gray-500 flex-shrink-0">
          💡 Tip: Trade items in the Grand Exchange for coins!
        </div>
      </div>
    </div>
  );
}
